"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.createEstLiqPriceSubject = void 0;
const rxjs_1 = require("rxjs");
const FutureAPI_1 = require("../../../apis/FutureAPI");
const CustomAssetsProvider_1 = require("../../Assets/CustomAssetsProvider");
const LiquidationCalculator_1 = require("../../Calculator/LiquidationCalculator");
const interface_1 = require("../../../interface");
const trade_utils_lib_1 = require("trade_utils_lib");
const operators_1 = require("rxjs/operators");
const LeverageConfig_1 = require("../../FuturesConfig/FutureLeverageProvider/LeverageConfig");
function combineDataSource(symbolInfo) {
    return (0, rxjs_1.combineLatest)([
        (0, CustomAssetsProvider_1.getCustomAssetsObservable)(),
        (0, trade_utils_lib_1.requestWithRetry)(() => (0, FutureAPI_1.queryFutureFeeRate)(symbolInfo.base, symbolInfo.quote).pipe((0, operators_1.map)(({ maker_fee_rate, taker_fee_rate }) => ({
            makerFeeRate: maker_fee_rate,
            takerFeeRate: taker_fee_rate,
        })))),
        (0, LeverageConfig_1.queryLeverageConfigureWithCache)(symbolInfo.symbol),
    ]);
}
/**
 * 生成Observable
 * @param symbolInfo
 * @param destroy 销毁函数，在Observable被取消订阅时调用
 */
function withBehaviorObservable(symbolInfo, destroy) {
    return new rxjs_1.Observable((obs) => {
        const sub = combineDataSource(symbolInfo).subscribe({
            next: (params) => {
                obs.next(params);
            },
            error: () => { },
            complete: () => obs.complete(),
        });
        return () => {
            sub.unsubscribe();
            destroy();
        };
    }).pipe((0, operators_1.publishBehavior)([]), (0, operators_1.refCount)());
}
// 缓存数据源的map
function cachedManager() {
    // 数据源的内存缓存，key为symbol,value为数据源
    const cachedMap = new Map();
    // 从缓存中获取某个symbol的数据源
    function getCachedObservable(symbolInfo) {
        // 无法从缓存中获取到数据源
        if (!cachedMap.get(symbolInfo.symbol)) {
            // 使用withBehaviorObservable新建数据源，并设置到缓存中
            cachedMap.set(symbolInfo.symbol, withBehaviorObservable(symbolInfo, () => {
                // 当Observable被取消订阅时，自动从缓存中删除该Observable
                cachedMap.delete(symbolInfo.symbol);
            }));
        }
        // 直接从缓存中获取到数据源
        return cachedMap.get(symbolInfo.symbol);
    }
    // 清空所有的数据源
    function cleanCache() {
        cachedMap.clear();
    }
    return {
        getCachedObservable,
        cleanCache,
    };
}
let instance = null;
function getInstance() {
    if (!instance) {
        instance = cachedManager();
    }
    return instance;
}
/**
 * 获取挂单计算预估强平价的Observable
 * @param symbolInfo 币种的symbolInfo
 * @param subject 用来传递属性的的subject
 */
function getOrderEstLiqPriceObservable(symbolInfo, subject) {
    // 合并数据源以及subject
    return (0, rxjs_1.combineLatest)([getInstance().getCachedObservable(symbolInfo), subject.asObservable().pipe((0, operators_1.startWith)(undefined))]).pipe((0, operators_1.map)(([dataSource, props]) => {
        var _a;
        if (!dataSource || !dataSource.length || !props || props.reduceOnly) {
            return "";
        }
        const [accountDetail, feeRate, leverageConfig] = dataSource;
        const { marginType, baseInvest, price, leverage, symbolInfo, postOnly, orderSide, positionSide } = props;
        const tradePriceD = new trade_utils_lib_1.SafeDecimal(price);
        const crossBalance = accountDetail.balanceMap.get(symbolInfo.quote);
        /**计算挂单占用保证金 */
        const orderMargin = new trade_utils_lib_1.SafeDecimal(baseInvest).mul(tradePriceD).div(new trade_utils_lib_1.SafeDecimal(leverage));
        const available = marginType === interface_1.FutureMarginType.CROSS ? orderMargin.add(new trade_utils_lib_1.SafeDecimal(crossBalance === null || crossBalance === void 0 ? void 0 : crossBalance.available)) : orderMargin;
        const isMakerOrder = postOnly;
        /** 预估交易量 */
        const esVolume = new trade_utils_lib_1.SafeDecimal(baseInvest).mul(tradePriceD);
        const fee = esVolume.mul(isMakerOrder ? new trade_utils_lib_1.SafeDecimal(feeRate.makerFeeRate) : new trade_utils_lib_1.SafeDecimal(feeRate.takerFeeRate));
        const _estLiqPrice = LiquidationCalculator_1.LiquidationCalculator.calcuLiquidationPrice({
            leverageConfigs: (leverageConfig === null || leverageConfig === void 0 ? void 0 : leverageConfig.rows) || [],
            position: baseInvest,
            avgPrice: tradePriceD,
            side: orderSide === interface_1.CustomOrderSide.BUY ? "long" : "short",
            assets: available.toFixed(),
            otherSpending: fee,
        });
        const rounding = positionSide === interface_1.FuturePositionSide.SHORT ? trade_utils_lib_1.SafeDecimal.ROUND_FLOOR : trade_utils_lib_1.SafeDecimal.ROUND_CEIL;
        // FIX 使用toFixed避免预估强平价被显示成科学计数法
        return _estLiqPrice.eq(0) ? "" : _estLiqPrice.toFixed((_a = symbolInfo.precision) !== null && _a !== void 0 ? _a : 2, rounding);
    }));
}
/**
 * 工厂方法：生成计算预估强平价的实例
 * @returns next 用来传入新的属性
 * @returns getObservable 获取数据源用来订阅
 */
function createEstLiqPriceSubject() {
    const subject = new rxjs_1.Subject();
    function next(props) {
        subject.next(props);
    }
    function getObservable(symbolInfo) {
        return getOrderEstLiqPriceObservable(symbolInfo, subject);
    }
    return {
        next,
        getObservable,
    };
}
exports.createEstLiqPriceSubject = createEstLiqPriceSubject;
